import type {
  AnalysisSpec,
  AnalyticsBootstrap,
  ChartTemplate,
  Dataset,
} from '@lognai/plugin-analytics';
import { businessLabel } from '../lib/user-facing';

export type CreatorCatalog = AnalyticsBootstrap['semanticCatalog'][string];
export const datasetViewFor = (id: string): string => `dataset_${id.replace(/-/g, '')}`;

export const compatibleTemplatesFor = (
  supported: ChartTemplate[],
  measures: string[],
  dimensions: string[],
  timeDimension?: string,
  baselineComparison = false
): ChartTemplate[] =>
  supported.filter((template) => {
    if (!measures.length) return false;
    if (baselineComparison) return ['stacked_column', 'horizontal_bar', 'metric_panel', 'table'].includes(template);
    if (template === 'metric_panel') return dimensions.length === 0;
    if (template === 'table') return dimensions.length >= 1;
    const hasCategory = dimensions.length > 0 || Boolean(timeDimension);
    if (template === 'area' || template === 'stacked_area' || template === 'calendar_heatmap')
      return Boolean(timeDimension);
    if (template === 'bubble') return measures.length >= 3 && dimensions.length >= 1;
    if (template === 'sankey' || template === 'heatmap') return dimensions.length >= 2;
    if (template === 'gantt') return dimensions.length >= 3;
    if (template === 'diverging_bar') return measures.length >= 2 && dimensions.length >= 1;
    if (template === 'stacked_column') return hasCategory;
    return dimensions.length >= 1;
  });

export const recommendTemplate = (
  compatible: ChartTemplate[],
  analysis: Pick<AnalysisSpec, 'measures' | 'dimensions' | 'timeDimension'>
): { template?: ChartTemplate; reason: string } => {
  const candidates: Array<[ChartTemplate, string]> = analysis.timeDimension
    ? analysis.dimensions.length || analysis.measures.length > 1
      ? [
          ['stacked_column', 'Compare groups over time.'],
          ['area', 'See how values change over time.'],
        ]
      : [['area', 'See how values change over time.']]
    : analysis.dimensions.length >= 2
      ? [
          ['heatmap', 'Compare values across two breakdowns.'],
          ['horizontal_bar', 'Compare ranked categories.'],
        ]
      : [['horizontal_bar', 'Compare ranked categories.']];
  const recommended = candidates.find(([template]) => compatible.includes(template));
  return recommended
    ? { template: recommended[0], reason: recommended[1] }
    : { template: compatible[0], reason: 'Fits your selected metrics and breakdown.' };
};

export const memberLabel = (member: string, catalog?: CreatorCatalog): string =>
  catalog?.measures[member]?.label ?? catalog?.fieldLabels?.[member] ?? businessLabel(member);

export const initialAnalysis = (
  view: string,
  catalog?: CreatorCatalog,
  dataset?: Dataset
): AnalysisSpec => {
  const measures = Object.keys(catalog?.measures ?? {});
  const measure = measures.find((member) => !member.endsWith('.count')) ?? measures[0];
  const textField = dataset?.fields.find((field) => ['text', 'boolean'].includes(field.type));
  const dimension = textField ? `${view}.${textField.id}` : catalog?.dimensions[0];
  return {
    title: `${catalog?.title ?? businessLabel(view)} analysis`,
    view,
    measures: measure ? [measure] : [],
    dimensions: dimension ? [dimension] : [],
    template: 'horizontal_bar',
    filters: [],
    order: measure ? [{ member: measure, direction: 'desc' }] : [],
    limit: 10,
    drillPath: dimension ? [dimension] : [],
  };
};

/** Saving metadata never changes the query the user has reviewed. */
export const analysisKey = (analysis: AnalysisSpec): string =>
  JSON.stringify({
    view: analysis.view,
    measures: analysis.measures,
    dimensions: analysis.dimensions,
    timeDimension: analysis.timeDimension,
    filters: analysis.filters,
    order: analysis.order ?? [],
    limit: analysis.limit ?? 50,
    template: analysis.template,
    drillPath: analysis.drillPath ?? [],
    calculatedMetrics: analysis.calculatedMetrics,
    baselineComparison: analysis.baselineComparison,
    display: analysis.display,
  });

export const analysisDomains = (view: string): string[] => {
  if (view.startsWith('dataset_')) return ['datasets'];
  if (view.startsWith('inventory')) return ['inventory', 'products'];
  if (view.startsWith('warehouse')) return ['warehouse'];
  if (view.startsWith('shipment')) return ['shipments'];
  if (view.startsWith('procurement') || view.startsWith('supplier')) return ['procurement'];
  if (view.startsWith('product')) return ['products', 'orders', 'inventory'];
  return ['orders'];
};

export const starterQuestions = (catalog?: CreatorCatalog): string[] => {
  if (!catalog) return [];
  const metric =
    Object.values(catalog.measures).find((info) => info.label !== 'Rows')?.label ??
    Object.values(catalog.measures)[0]?.label;
  if (!metric) return [];
  const category = catalog.dimensions.find((member) => !catalog.timeDimensions.includes(member));
  return [
    ...(category ? [`Show the top 10 ${memberLabel(category, catalog)} by ${metric}`] : []),
    ...(catalog.timeDimensions.length
      ? [`Show ${metric} by month using ${memberLabel(catalog.timeDimensions[0], catalog)}`]
      : []),
    ...(category ? [`Compare ${metric} across ${memberLabel(category, catalog)}`] : []),
  ];
};

export const validDateRange = (range?: [string, string]): boolean =>
  !range ||
  (range.every(
    (date) =>
      /^\d{4}-\d{2}-\d{2}$/.test(date) &&
      !Number.isNaN(Date.parse(date)) &&
      new Date(date).toISOString().slice(0, 10) === date
  ) &&
    range[0] <= range[1]);
